/**
 * WorkspaceStageRail — compact stage stepper for the lead workspace.
 *
 * The active project name is repeated in the rail header so context is never
 * lost while scrolling or switching steps. Visual language:
 *   solid = completed · ringed = active (current) · muted = locked · struck = skipped.
 * The *selected* (viewing) step is outlined separately from the *active* step.
 * All statuses come from the portal stage engine — never computed locally.
 */

import React from 'react';
import type { PortalStage } from '../../types';
import type { PortalStepStatus } from '../../utils/portalStageEngine';
import { PORTAL_STAGE_FULL_LABELS } from '../../utils/portalStageEngine';
import { Check, Pin, X } from 'lucide-react';
import './WorkspaceStageRail.css';

export interface RailStep {
  id: PortalStage;
  number: number;
  title: string;
  shortTitle: string;
  icon: React.ReactNode;
}

interface WorkspaceStageRailProps {
  steps: RailStep[];
  statuses: Record<PortalStage, PortalStepStatus>;
  selectedStep: PortalStage;
  onSelectStep: (stage: PortalStage) => void;
  /** e.g. "#0042 · Lake House" — always visible so the project is never ambiguous. */
  activeProjectLabel: string;
  displayStage: PortalStage;
  /** True when a manual retreat pinned the stage (auto-bumps paused). */
  manualOverride?: boolean;
}

const WorkspaceStageRail: React.FC<WorkspaceStageRailProps> = ({
  steps,
  statuses,
  selectedStep,
  onSelectStep,
  activeProjectLabel,
  displayStage,
  manualOverride,
}) => {
  const activeIdx = steps.findIndex(s => s.id === displayStage);

  return (
    <div className="cw-stage-rail" data-tour="cw-stage-rail">
      <div className="cw-stage-rail-header">
        <span className="cw-stage-rail-project" title={activeProjectLabel}>
          {activeProjectLabel}
        </span>
        <span className="cw-stage-rail-current">
          Current stage: <strong>{PORTAL_STAGE_FULL_LABELS[displayStage]}</strong>
        </span>
        {manualOverride && (
          <span
            className="cw-stage-rail-pinned"
            title="Stage was manually retreated — released models, sent proposals, and payments will not auto-advance the timeline until you advance again."
          >
            <Pin size={11} /> Pinned
          </span>
        )}
      </div>
      <div className="cw-stage-rail-track" role="tablist" aria-label="Portal stages">
        {steps.map((step, index) => {
          const status = statuses[step.id];
          const isSelected = selectedStep === step.id;
          return (
            <React.Fragment key={step.id}>
              {index > 0 && (
                <div
                  className={`cw-rail-connector ${index <= activeIdx ? 'filled' : ''}`}
                  aria-hidden
                />
              )}
              <button
                type="button"
                role="tab"
                aria-selected={isSelected}
                className={`cw-rail-step ${status} ${isSelected ? 'selected' : ''}`}
                onClick={() => onSelectStep(step.id)}
                title={`${step.title} — ${status}`}
              >
                <span className="cw-rail-step-icon">
                  {status === 'completed' ? <Check size={13} /> : status === 'skipped' ? <X size={11} /> : step.icon}
                </span>
                <span className="cw-rail-step-label">{step.shortTitle}</span>
              </button>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default WorkspaceStageRail;
